import { Injectable, NgZone } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

/**
 * Microfone do simulador: dispositivos, permissão, medidor de nível e gravação.
 *
 * Fica fora do componente de chat de propósito — aqui é tudo API de navegador
 * (getUserMedia, MediaRecorder, AnalyserNode) e o componente só desenha.
 *
 * O diagnóstico segue a ideia do Google Meet: o problema tem nome ("o sistema
 * desativou o microfone"), aparece no próprio botão e vem com a próxima ação —
 * em vez de um "erro ao acessar o microfone" que não ajuda ninguém.
 */

export type CodigoDoProblema =
  | 'sem_suporte'
  | 'contexto_inseguro'
  | 'permissao_negada'
  | 'sem_microfone'
  | 'microfone_ocupado'
  | 'mudo_no_sistema'
  | 'dispositivo_sumiu'
  | 'sem_captacao';

export interface ProblemaDoMicrofone {
  codigo: CodigoDoProblema;
  titulo: string;
  descricao: string;
  /** Rótulo do botão do aviso; sem rótulo, o aviso é só informativo. */
  rotuloAcao?: string;
  acao?: 'tentar_de_novo' | 'escolher_microfone';
  /**
   * Impede gravar de verdade (sem permissão, sem dispositivo, mudo no
   * sistema). Aviso sem isto é só uma dica — gravar continua liberado.
   */
  bloqueia?: boolean;
}

export interface MicrofoneDisponivel {
  deviceId: string;
  rotulo: string;
}

export interface AudioGravado {
  blob: Blob;
  url: string;
  /** O Angular sanitiza blob: em [src]; o player usa esta, montada aqui. */
  urlSegura: SafeUrl;
  mimeType: string;
  duracaoSegundos: number;
  /** Nenhum pico acima do piso de ruído: o áudio saiu mudo. */
  mudo: boolean;
  /**
   * Forma de onda REAL da gravação (0..1 por fatia), lida do próprio arquivo.
   * Vem vazia quando o navegador não decodifica o formato — aí a bolha cai no
   * pontilhado em vez de mentir um desenho.
   */
  picos: number[];
}

/** Quantas fatias a onda da bolha tem. */
export const FATIAS_DA_ONDA = 32;

const CHAVE_DISPOSITIVO = 'mia.simulador.microfone';
const CHAVE_CONFERIDO = 'mia.simulador.microfone.conferido';
/** RMS abaixo disso é silêncio — fala normal passa de 0.05 com folga. */
const PISO_DE_RUIDO = 0.012;
/**
 * Quanto esperar antes de dizer que ainda não ouvimos nada. Só vale ANTES do
 * primeiro som: depois que o microfone provou que funciona, ficar quieto é
 * comportamento normal e nunca vira aviso (era o que atrapalhava aqui — o
 * Meet também não acusa nada quando você simplesmente para de falar).
 */
const MS_PARA_ACUSAR_SILENCIO = 5000;
/**
 * Quanto um `muted` precisa PERSISTIR antes de virar acusação. Track recém-
 * aberta nasce muted até o primeiro frame chegar — ler o estado no instante em
 * que o stream nasce acusava um microfone perfeitamente vivo.
 */
export const MS_PARA_CONFIRMAR_MUDO = 1200;
/**
 * Bitrate da gravação. O padrão do Chrome (~128kbps) faz um recado de 2 minutos
 * passar de 2MB, e o áudio sobe em base64 dentro do JSON — no teto de 5mb do
 * Servidor isso aperta. 32kbps de opus é a faixa dos áudios do WhatsApp e
 * transcreve igual: 120s ficam em ~480KB.
 */
export const BITRATE_DE_VOZ = 32000;

export const LIMITE_DE_GRAVACAO_SEGUNDOS = 120;

@Injectable({ providedIn: 'root' })
export class GravadorDeAudioService {
  microfones: MicrofoneDisponivel[] = [];
  deviceIdEscolhido: string | null = null;
  problema: ProblemaDoMicrofone | null = null;
  monitorando = false;
  gravando = false;
  /** Pausada: a captação parou, mas a gravação continua viva e retomável. */
  pausado = false;

  /** Nível 0..1 ~60x/s, FORA do zone: quem ouve escreve direto no DOM. */
  aoMudarNivel: ((nivel: number) => void) | null = null;

  private stream: MediaStream | null = null;
  private contexto: AudioContext | null = null;
  private analisador: AnalyserNode | null = null;
  private amostras: Uint8Array | null = null;
  private frame = 0;
  private gravador: MediaRecorder | null = null;
  private pedacos: Blob[] = [];
  private inicioDaGravacao = 0;
  private msPausados = 0;
  private pausadoDesde = 0;
  private picoDaGravacao = 0;
  private silencioDesde = 0;
  /** Já entrou som desde que este microfone foi aberto: ele está provado. */
  private jaCaptouSom = false;
  /** Quando o último som acima do piso entrou: veta acusação de mute. */
  private ultimoSomEm = 0;
  private confirmacaoDeMudo: any = null;

  constructor(private zone: NgZone, private sanitizador: DomSanitizer) {
    this.deviceIdEscolhido = this.leiaDispositivoSalvo();
  }

  get suportado(): boolean {
    return !!(navigator?.mediaDevices?.getUserMedia && typeof (window as any).MediaRecorder === 'function');
  }

  /**
   * Permissão já concedida antes? Serve para decidir se o primeiro clique no
   * microfone grava direto ou abre o painel para escolher e conferir o
   * dispositivo. Navegador sem a API de permissões cai no painel na 1ª vez.
   */
  async permissaoJaConcedida(): Promise<boolean> {
    const permissoes: any = (navigator as any).permissions;
    if (!permissoes?.query) {
      return this.leiaBandeiraDeConferido();
    }

    try {
      const estado = await permissoes.query({ name: 'microphone' as any });
      return estado?.state === 'granted';
    } catch {
      return this.leiaBandeiraDeConferido();
    }
  }

  /** O painel foi conferido nesta máquina (fallback de quem não tem a API). */
  marqueComoConferido(): void {
    try {
      localStorage.setItem(CHAVE_CONFERIDO, '1');
    } catch { /* modo anônimo: pergunta de novo na próxima */ }
  }

  private leiaBandeiraDeConferido(): boolean {
    try {
      return localStorage.getItem(CHAVE_CONFERIDO) === '1';
    } catch {
      return false;
    }
  }

  /**
   * Liga o microfone só para medir (não grava): é o que dá o feedback ao vivo
   * antes de gravar e o que descobre um microfone mudo sem gastar uma gravação.
   */
  async abraMonitor(): Promise<boolean> {
    if (this.monitorando) return true;
    if (!this.confiraSuporte()) return false;

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: this.restricoesDeAudio() });
    } catch (erro) {
      this.registreProblemaDeAcesso(erro);
      return false;
    }

    this.limpeProblema();
    await this.recarregueMicrofones();
    this.vigieODispositivo();
    this.comeceAMedir();
    this.monitorando = true;
    return true;
  }

  fecheMonitor(): void {
    this.pareDeMedir();
    this.solteStream();
    this.monitorando = false;
  }

  /** Lista os microfones; os nomes só existem depois da permissão concedida. */
  async recarregueMicrofones(): Promise<MicrofoneDisponivel[]> {
    if (!navigator?.mediaDevices?.enumerateDevices) return this.microfones;

    const dispositivos = await navigator.mediaDevices.enumerateDevices().catch(() => [] as MediaDeviceInfo[]);
    this.microfones = dispositivos
      .filter(dispositivo => dispositivo.kind === 'audioinput')
      .map((dispositivo, indice) => ({
        deviceId: dispositivo.deviceId,
        rotulo: dispositivo.label || `Microfone ${indice + 1}`,
      }));

    if (!this.microfones.length) {
      this.registreProblema({
        codigo: 'sem_microfone',
        titulo: 'Nenhum microfone encontrado',
        descricao: 'Conecte um microfone e atualize a lista.',
        bloqueia: true,
        rotuloAcao: 'Procurar de novo',
        acao: 'tentar_de_novo',
      });
    } else if (this.deviceIdEscolhido &&
               !this.microfones.some(microfone => microfone.deviceId === this.deviceIdEscolhido)) {
      // o escolhido saiu da tomada: volta ao padrão em vez de falhar na gravação
      this.deviceIdEscolhido = null;
      this.limpeDispositivoSalvo();
    }

    return this.microfones;
  }

  /** Troca o microfone e já religa o monitor no novo (o nível responde na hora). */
  async escolhaMicrofone(deviceId: string): Promise<boolean> {
    this.deviceIdEscolhido = deviceId || null;
    this.salveDispositivo(this.deviceIdEscolhido);

    if (!this.monitorando) return true;

    this.fecheMonitor();
    return this.abraMonitor();
  }

  async comece(): Promise<boolean> {
    if (this.gravando) return true;
    if (!await this.abraMonitor()) return false;

    const mimeType = escolhaFormatoSuportado();
    try {
      this.gravador = mimeType
        ? new MediaRecorder(this.stream as MediaStream, { mimeType, audioBitsPerSecond: BITRATE_DE_VOZ })
        : new MediaRecorder(this.stream as MediaStream, { audioBitsPerSecond: BITRATE_DE_VOZ });
    } catch {
      this.registreProblema({
        codigo: 'sem_suporte',
        titulo: 'Este navegador não grava áudio',
        descricao: 'Use o Chrome, o Edge ou o Safari mais recente para gravar pelo simulador.',
        bloqueia: true,
      });
      return false;
    }

    this.pedacos = [];
    this.picoDaGravacao = 0;
    this.inicioDaGravacao = Date.now();
    this.msPausados = 0;
    this.pausadoDesde = 0;
    this.pausado = false;
    this.gravador.ondataavailable = (evento: any) => {
      if (evento.data && evento.data.size > 0) this.pedacos.push(evento.data);
    };
    this.gravador.start();
    this.gravando = true;
    return true;
  }

  /**
   * Pausa sem encerrar: o MediaRecorder guarda o que já foi gravado e o
   * `retome()` continua NO MESMO arquivo. Pausar não pode custar a gravação —
   * era exatamente o que acontecia quando o botão parava em vez de pausar.
   */
  pause(): boolean {
    if (!this.gravador || !this.gravando || this.pausado) return false;

    try {
      this.gravador.pause();
    } catch {
      return false;
    }
    this.pausado = true;
    this.pausadoDesde = Date.now();
    return true;
  }

  /** Continua a mesma gravação de onde parou. */
  retome(): boolean {
    if (!this.gravador || !this.pausado) return false;

    try {
      this.gravador.resume();
    } catch {
      return false;
    }
    this.msPausados += Date.now() - this.pausadoDesde;
    this.pausadoDesde = 0;
    this.pausado = false;
    return true;
  }

  /**
   * Prévia do que já foi gravado, SEM encerrar a gravação: `requestData()`
   * entrega os pedaços acumulados e eles seguem valendo para o arquivo final.
   * Melhor esforço — navegador que não toque um webm parcial só deixa de tocar,
   * a gravação em si nunca corre risco.
   */
  async espieOParcial(): Promise<AudioGravado | null> {
    const gravador = this.gravador;
    if (!gravador || !this.pausado) return null;

    await new Promise<void>(resolva => {
      const aoReceberOriginal = gravador.ondataavailable;
      gravador.ondataavailable = (evento: any) => {
        if (evento.data && evento.data.size > 0) this.pedacos.push(evento.data);
        gravador.ondataavailable = aoReceberOriginal;
        resolva();
      };
      try {
        gravador.requestData();
      } catch {
        gravador.ondataavailable = aoReceberOriginal;
        resolva();
      }
    });

    return this.monteOAudio(gravador.mimeType || 'audio/webm');
  }

  /** Encerra e devolve o áudio; `null` quando não veio nada utilizável. */
  pare(): Promise<AudioGravado | null> {
    const gravador = this.gravador;
    if (!gravador || !this.gravando) return Promise.resolve(null);

    return new Promise<AudioGravado | null>(resolva => {
      gravador.onstop = () => {
        // monta antes de zerar o estado: a duração desconta o tempo pausado
        this.monteOAudio(gravador.mimeType || 'audio/webm').then(audio => {
          this.gravando = false;
          this.pausado = false;
          this.gravador = null;
          this.pedacos = [];
          resolva(audio);
        });
      };
      gravador.stop();
    });
  }

  /**
   * Empacota o que está nos pedaços — serve à prévia parcial e ao fim da
   * gravação. Não consome os pedaços: quem encerra é que os descarta.
   */
  private async monteOAudio(mimeType: string): Promise<AudioGravado | null> {
    const blob = new Blob(this.pedacos, { type: mimeType });
    if (!blob.size) return null;

    const url = URL.createObjectURL(blob);
    return {
      blob,
      url,
      urlSegura: this.sanitizador.bypassSecurityTrustUrl(url),
      mimeType,
      duracaoSegundos: this.duracaoDaGravacao(),
      // take silenciosa é avisada na própria barra ("saiu mudo"); não vira
      // alerta de dispositivo — o microfone pode estar perfeito
      mudo: this.picoDaGravacao < PISO_DE_RUIDO,
      picos: await this.calculeAOnda(blob),
    };
  }

  /** Tempo de áudio de fato capturado: o relógio menos as pausas. */
  duracaoDaGravacao(): number {
    const pausaEmCurso = (this.pausado && this.pausadoDesde) ? Date.now() - this.pausadoDesde : 0;
    const ms = Date.now() - this.inicioDaGravacao - this.msPausados - pausaEmCurso;
    return Math.max(1, Math.round(ms / 1000));
  }

  cancele(): void {
    if (this.gravador && this.gravando) {
      this.gravador.onstop = () => {};
      try { this.gravador.stop(); } catch { /* já parado */ }
    }
    this.gravador = null;
    this.pedacos = [];
    this.gravando = false;
    this.pausado = false;
  }

  /**
   * Forma de onda de verdade: decodifica o arquivo gravado e tira o pico de
   * cada fatia. Normaliza pelo pico da própria gravação, senão fala baixa vira
   * uma linha reta. Formato que o navegador não decodifica devolve [] — a
   * bolha mostra o pontilhado em vez de inventar um desenho.
   */
  private async calculeAOnda(blob: Blob): Promise<number[]> {
    const Contexto = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Contexto || !blob.arrayBuffer) return [];

    const contexto = new Contexto();
    try {
      const buffer: AudioBuffer = await contexto.decodeAudioData(await blob.arrayBuffer());
      const canal = buffer.getChannelData(0);
      const porFatia = Math.floor(canal.length / FATIAS_DA_ONDA) || 1;

      const picos: number[] = [];
      let maximo = 0;
      for (let fatia = 0; fatia < FATIAS_DA_ONDA; fatia++) {
        const inicio = fatia * porFatia;
        const fim = Math.min(inicio + porFatia, canal.length);
        let pico = 0;
        for (let i = inicio; i < fim; i++) {
          const amplitude = Math.abs(canal[i]);
          if (amplitude > pico) pico = amplitude;
        }
        picos.push(pico);
        if (pico > maximo) maximo = pico;
      }

      return maximo > 0 ? picos.map(pico => pico / maximo) : picos;
    } catch {
      return [];
    } finally {
      contexto.close().catch(() => {});
    }
  }

  /** Some com o áudio de teste/pré-envio sem vazar o blob URL. */
  descarte(audio: AudioGravado | null): void {
    if (audio?.url) URL.revokeObjectURL(audio.url);
  }

  limpeProblema(): void {
    this.problema = null;
  }

  // ==== nível ao vivo ====

  private comeceAMedir(): void {
    if (!this.stream) return;

    const Contexto = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Contexto) return;

    this.contexto = new Contexto();
    const fonte = this.contexto.createMediaStreamSource(this.stream);
    this.analisador = this.contexto.createAnalyser();
    this.analisador.fftSize = 1024;
    fonte.connect(this.analisador);
    this.amostras = new Uint8Array(this.analisador.fftSize);
    // microfone novo recomeça sem crédito: precisa provar que capta
    this.silencioDesde = Date.now();
    this.jaCaptouSom = false;
    this.ultimoSomEm = 0;

    // 60fps dentro do zone dispara change detection sem parar e trava o painel
    this.zone.runOutsideAngular(() => this.leiaONivel());
  }

  private leiaONivel = (): void => {
    if (!this.analisador || !this.amostras) return;

    this.analisador.getByteTimeDomainData(this.amostras);

    let soma = 0;
    for (let i = 0; i < this.amostras.length; i++) {
      const desvio = (this.amostras[i] - 128) / 128;
      soma += desvio * desvio;
    }
    const nivel = Math.sqrt(soma / this.amostras.length);

    if (this.gravando && nivel > this.picoDaGravacao) {
      this.picoDaGravacao = nivel;
    }

    this.confiraSilencio(nivel);
    if (this.aoMudarNivel) this.aoMudarNivel(Math.min(1, nivel * 4));

    this.frame = requestAnimationFrame(this.leiaONivel);
  };

  /**
   * O nível serve para UMA pergunta só: "este microfone já produziu algum som
   * desde que foi aberto?". Silêncio depois do primeiro som é só alguém calado
   * — acusar aí seria falso positivo (foi o que aconteceu na primeira versão).
   *
   * Microfone realmente desativado pelo sistema chega por outro caminho, o
   * estado da track (`muted`/`ended`), que é o sinal que o Meet usa — mas o
   * nível MANDA nele: som entrando desmente qualquer acusação de mute.
   */
  private confiraSilencio(nivel: number): void {
    if (nivel >= PISO_DE_RUIDO) {
      this.ultimoSomEm = Date.now();
      this.jaCaptouSom = true;
      this.canceleConfirmacaoDeMudo();
      // som acima do piso é prova de que a track entrega áudio: um microfone
      // desativado pelo sistema não deixaria passar nada. O `if` evita entrar
      // no zone 60x/s — só quando há mesmo um aviso para derrubar.
      if (this.problema?.codigo === 'sem_captacao' || this.problema?.codigo === 'mudo_no_sistema') {
        this.zone.run(() => this.limpeProblema());
      }
      return;
    }

    // já provou que funciona, ou já há um aviso na tela: nada a dizer
    if (this.jaCaptouSom || this.problema) return;
    if (Date.now() - this.silencioDesde < MS_PARA_ACUSAR_SILENCIO) return;

    this.zone.run(() => this.registreProblema(PROBLEMA_SEM_CAPTACAO));
  }

  /**
   * Estado do dispositivo, não do volume: o navegador marca a track como
   * `muted` quando o sistema desativa o microfone e `ended` quando ele some.
   * É por aqui que sai o aviso equivalente ao do Meet.
   */
  private vigieODispositivo(): void {
    const track = this.stream?.getAudioTracks()[0];
    if (!track) return;

    const avalie = () => this.zone.run(() => {
      // sumir é definitivo: dá para avisar na hora
      if (track.readyState === 'ended') {
        this.canceleConfirmacaoDeMudo();
        this.registreProblema(PROBLEMA_DISPOSITIVO_SUMIU);
        return;
      }
      // `enabled` fica de fora de propósito: quem mexe nele é código nosso, o
      // sistema nunca — ele jamais significa "o sistema desativou o microfone"
      if (track.muted) {
        this.agendeConfirmacaoDeMudo(track);
        return;
      }
      this.canceleConfirmacaoDeMudo();
      if (this.problema?.codigo === 'mudo_no_sistema' || this.problema?.codigo === 'dispositivo_sumiu') {
        this.limpeProblema();
      }
    });

    track.onmute = avalie;
    track.onunmute = avalie;
    track.onended = avalie;
    avalie();
  }

  /**
   * Um `muted` só vira aviso depois de PERSISTIR: track recém-aberta nasce
   * muted até o primeiro frame chegar, e acusar aí travava o painel de um
   * microfone que estava gravando normalmente. Destravar, captar som ou fechar
   * o monitor cancelam a acusação antes que ela chegue à tela.
   */
  private agendeConfirmacaoDeMudo(track: MediaStreamTrack): void {
    if (this.confirmacaoDeMudo) return;

    const agendadoEm = Date.now();
    this.confirmacaoDeMudo = setTimeout(() => {
      this.confirmacaoDeMudo = null;
      // stream trocado no meio da janela: quem manda é a track de agora
      if (this.stream?.getAudioTracks()[0] !== track) return;
      if (track.readyState === 'ended' || !track.muted) return;
      if (this.ultimoSomEm >= agendadoEm) return; // captou som na janela: está vivo
      this.zone.run(() => this.registreProblema(PROBLEMA_MUDO_NO_SISTEMA));
    }, MS_PARA_CONFIRMAR_MUDO);
  }

  private canceleConfirmacaoDeMudo(): void {
    if (!this.confirmacaoDeMudo) return;
    clearTimeout(this.confirmacaoDeMudo);
    this.confirmacaoDeMudo = null;
  }

  private pareDeMedir(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.analisador = null;
    this.amostras = null;
    if (this.contexto) {
      this.contexto.close().catch(() => {});
      this.contexto = null;
    }
    if (this.aoMudarNivel) this.aoMudarNivel(0);
  }

  private solteStream(): void {
    this.canceleConfirmacaoDeMudo();
    // sem parar as tracks o navegador deixa a luz do microfone acesa
    this.stream?.getTracks().forEach(track => {
      track.onmute = null;
      track.onunmute = null;
      track.onended = null;
      track.stop();
    });
    this.stream = null;
  }

  // ==== diagnóstico ====

  private confiraSuporte(): boolean {
    if (this.suportado) return true;

    // getUserMedia só existe em https ou localhost
    const seguro = (window as any).isSecureContext !== false;
    this.registreProblema(seguro ? {
      codigo: 'sem_suporte',
      titulo: 'Este navegador não grava áudio',
      descricao: 'Use o Chrome, o Edge ou o Safari mais recente para gravar pelo simulador.',
      bloqueia: true,
    } : {
      codigo: 'contexto_inseguro',
      bloqueia: true,
      titulo: 'O navegador bloqueia o microfone aqui',
      descricao: 'O microfone só é liberado em endereços https (ou localhost). Abra o painel pelo endereço seguro.',
    });
    return false;
  }

  private registreProblemaDeAcesso(erro: any): void {
    const nome = String(erro?.name || '');

    if (nome === 'NotAllowedError' || nome === 'SecurityError') {
      this.registreProblema({
        codigo: 'permissao_negada',
        bloqueia: true,
        titulo: 'O navegador bloqueou o microfone',
        descricao: 'Clique no cadeado ao lado do endereço, libere o microfone para este site e tente de novo.',
        rotuloAcao: 'Tentar de novo',
        acao: 'tentar_de_novo',
      });
      return;
    }

    if (nome === 'NotFoundError' || nome === 'OverconstrainedError') {
      this.registreProblema({
        codigo: 'sem_microfone',
        titulo: 'Nenhum microfone encontrado',
        descricao: 'Conecte um microfone e tente de novo — se acabou de conectar, atualize a lista.',
        bloqueia: true,
        rotuloAcao: 'Procurar de novo',
        acao: 'tentar_de_novo',
      });
      return;
    }

    this.registreProblema({
      codigo: 'microfone_ocupado',
      bloqueia: true,
      titulo: 'O microfone não abriu',
      descricao: 'Outro programa pode estar usando o microfone. Feche a chamada aberta ou escolha outro dispositivo.',
      rotuloAcao: 'Escolher microfone',
      acao: 'escolher_microfone',
    });
  }

  /**
   * Só grava o diagnóstico: quem chama já está dentro do zone do Angular (ação
   * do usuário) ou embrulha a chamada em `zone.run` (o detector de silêncio).
   */
  private registreProblema(problema: ProblemaDoMicrofone): void {
    this.problema = problema;
  }

  private restricoesDeAudio(): MediaTrackConstraints | boolean {
    return this.deviceIdEscolhido ? { deviceId: { exact: this.deviceIdEscolhido } } : true;
  }

  // ==== dispositivo lembrado entre sessões ====

  private leiaDispositivoSalvo(): string | null {
    try {
      return localStorage.getItem(CHAVE_DISPOSITIVO);
    } catch {
      return null;
    }
  }

  private salveDispositivo(deviceId: string | null): void {
    try {
      if (deviceId) localStorage.setItem(CHAVE_DISPOSITIVO, deviceId);
      else localStorage.removeItem(CHAVE_DISPOSITIVO);
    } catch { /* modo anônimo/bloqueado: só não lembra */ }
  }

  private limpeDispositivoSalvo(): void {
    this.salveDispositivo(null);
  }
}

/** Ainda não ouvimos nada: é uma dica, não uma acusação de defeito. */
const PROBLEMA_SEM_CAPTACAO: ProblemaDoMicrofone = {
  codigo: 'sem_captacao',
  titulo: 'Ainda não ouvi nada por aqui',
  descricao: 'Fale alguma coisa para conferir. Se as barras continuarem paradas, o microfone pode estar mudo no sistema ou o dispositivo pode ser outro.',
  rotuloAcao: 'Escolher microfone',
  acao: 'escolher_microfone',
};

/** O sistema tirou o microfone da mão do navegador — o caso do Meet. */
const PROBLEMA_MUDO_NO_SISTEMA: ProblemaDoMicrofone = {
  codigo: 'mudo_no_sistema',
  bloqueia: true,
  titulo: 'O microfone foi desativado pelo sistema',
  descricao: 'Reative o microfone nas configurações de som do computador (ou tire o mudo do headset) e tente de novo.',
  rotuloAcao: 'Tentar de novo',
  acao: 'tentar_de_novo',
};

const PROBLEMA_DISPOSITIVO_SUMIU: ProblemaDoMicrofone = {
  codigo: 'dispositivo_sumiu',
  bloqueia: true,
  titulo: 'O microfone foi desconectado',
  descricao: 'O dispositivo que estava em uso saiu. Conecte de novo ou escolha outro.',
  rotuloAcao: 'Escolher microfone',
  acao: 'escolher_microfone',
};

/** WhatsApp manda ogg/opus; o navegador dá webm/opus ou mp4 — os dois servem. */
function escolhaFormatoSuportado(): string {
  const candidatos = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  const MediaRecorderRef: any = (window as any).MediaRecorder;
  if (!MediaRecorderRef?.isTypeSupported) return '';

  return candidatos.find(candidato => MediaRecorderRef.isTypeSupported(candidato)) || '';
}
